/**
 * Categories and tags for the room and task libraries.
 *
 * The codes are the stable identity. Labels live in the translation catalogs
 * (`libraryTaxonomy.*`) so a Spanish estimator does not browse English
 * headings. A company can change a template's category; the seed and the
 * migration only take a best guess.
 */

import type { FloorType } from './index.ts';
import { FLOOR_TYPES } from './index.ts';

export const ROOM_CATEGORIES = [
  'living',
  'kitchens',
  'restrooms',
  'bedrooms',
  'offices',
  'common',
  'entries',
  'laundry',
  'outdoor',
  'specialty',
] as const;
export type RoomCategory = (typeof ROOM_CATEGORIES)[number];

export const TASK_CATEGORIES = [
  'dusting',
  'floors',
  'restrooms',
  'kitchen',
  'glass',
  'trash',
  'disinfecting',
  'deep',
  'specialty',
] as const;
export type TaskCategory = (typeof TASK_CATEGORIES)[number];

export const TEMPLATE_SEGMENTS = ['residential', 'commercial'] as const;
export type TemplateSegment = (typeof TEMPLATE_SEGMENTS)[number];

export const TEMPLATE_JOB_TYPES = ['standard', 'deep', 'move_out', 'post_construction'] as const;
export type TemplateJobType = (typeof TEMPLATE_JOB_TYPES)[number];

export interface TemplateTags {
  segments: TemplateSegment[];
  jobTypes: TemplateJobType[];
  floorTypes: FloorType[];
}

function asList<T extends string>(value: unknown, allowed: readonly T[]): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is T => typeof item === 'string' && (allowed as readonly string[]).includes(item));
}

export function parseTemplateTags(raw: string | null | undefined): TemplateTags | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<TemplateTags>;
    const tags: TemplateTags = {
      segments: asList(parsed.segments, TEMPLATE_SEGMENTS),
      jobTypes: asList(parsed.jobTypes, TEMPLATE_JOB_TYPES),
      floorTypes: asList(parsed.floorTypes, FLOOR_TYPES),
    };
    if (tags.segments.length === 0 && tags.jobTypes.length === 0 && tags.floorTypes.length === 0) return null;
    return tags;
  } catch {
    return null;
  }
}

export function serializeTemplateTags(tags: TemplateTags | null | undefined): string | null {
  if (!tags) return null;
  const normalized: TemplateTags = {
    segments: asList(tags.segments, TEMPLATE_SEGMENTS),
    jobTypes: asList(tags.jobTypes, TEMPLATE_JOB_TYPES),
    floorTypes: asList(tags.floorTypes, FLOOR_TYPES),
  };
  if (normalized.segments.length === 0 && normalized.jobTypes.length === 0 && normalized.floorTypes.length === 0) {
    return null;
  }
  return JSON.stringify(normalized);
}

export function emptyTemplateTags(): TemplateTags {
  return { segments: [], jobTypes: [], floorTypes: [] };
}

/** First matching rule wins. Patterns are lowercase and safe in MySQL REGEXP. */
export const TASK_CATEGORY_RULES: readonly { readonly category: TaskCategory; readonly pattern: string }[] = [
  {
    category: 'deep',
    pattern:
      'high dust|polvo en altura|baseboard|zocalo|zócalo|grout|lejada|cobweb|telara|blind|persiana|extraction|extracci|strip and wax|encerado|auto-scrub|fregado mec',
  },
  {
    category: 'restrooms',
    pattern: 'toilet|urinal|shower|bath|baño|inodoro|urinario|lavabo|ducha|sanitario|vanity|fixture|mueble',
  },
  {
    category: 'kitchen',
    pattern:
      'oven|fridge|refriger|stove|counter|cabinet|microwave|dishwasher|backsplash|cocina|horno|estufa|encimera|gabinete|microonda|lavavajilla|backsplash|comedor y la tarja|break room',
  },
  { category: 'trash', pattern: 'trash|basura|waste bin|bote' },
  { category: 'glass', pattern: 'mirror|glass|espejo|cristal|window|ventana' },
  { category: 'floors', pattern: 'vacuum|mop|sweep|floor|aspir|trapear|barrer|piso|alfombr|buff|pulir' },
  { category: 'dusting', pattern: 'dust|polvo' },
  { category: 'disinfecting', pattern: 'disinfect|desinfect|sanitiz|switch|interruptor|handle|manija|touch|contacto' },
];

export const ROOM_CATEGORY_RULES: readonly { readonly category: RoomCategory; readonly pattern: string }[] = [
  { category: 'kitchens', pattern: 'kitchen|cocina|break room|cocineta|kitchenette' },
  { category: 'restrooms', pattern: 'bath|baño|restroom|sanitario|toilet|aseo' },
  { category: 'bedrooms', pattern: 'bed|dormitorio|closet|armario|clóset' },
  { category: 'offices', pattern: 'office|oficina|cubicle|cubículo|conference|junta|classroom|aula|desk' },
  { category: 'laundry', pattern: 'laundry|lavander|utility|servicio' },
  { category: 'outdoor', pattern: 'garage|garaje|patio|balcony|balcón|outdoor' },
  {
    category: 'entries',
    pattern:
      'stair|escalera|hallway|pasillo|entry|entrada|lobby|recepción|corridor|corredor|elevator|elevador|ascensor',
  },
  { category: 'common', pattern: 'common|áreas comunes|mail|correo|gym|gimnasio|locker|vestidor' },
  { category: 'living', pattern: 'living|sala|dining|comedor|family' },
  {
    category: 'specialty',
    pattern:
      'warehouse|bodega|medical|médic|exam|retail|tienda|custom|personalizada|basement|sótano|trash room|cuarto de basura',
  },
];

function firstMatch<T extends string>(
  rules: readonly { readonly category: T; readonly pattern: string }[],
  text: string,
): T | null {
  const haystack = text.toLowerCase();
  for (const rule of rules) {
    if (new RegExp(rule.pattern, 'i').test(haystack)) return rule.category;
  }
  return null;
}

/**
 * Best-guess task category from a template's own name (and, when the name is
 * the ambiguous Spanish "comedor", its description is not used — tasks are
 * named after the work).
 */
export function guessTaskCategory(name: string): TaskCategory {
  // These names contain a restroom word ("sink") but are not restroom work.
  if (/break room|kitchenette|cocineta|comedor y la tarja/i.test(name)) return 'kitchen';
  if (/utility sink|washer|dryer|lint trap|secadora|lavadora/i.test(name)) return 'specialty';
  return firstMatch(TASK_CATEGORY_RULES, name) ?? 'specialty';
}

/**
 * Best-guess room category. `description` breaks the one real collision:
 * residential "Comedor" is a dining room, commercial "Comedor" is a break room.
 */
export function guessRoomCategory(name: string, description = ''): RoomCategory {
  const combined = `${name} ${description}`;
  if (/^comedor$/i.test(name.trim()) && /cocineta|kitchenette|coffee|café/i.test(description)) return 'kitchens';
  if (/^comedor$/i.test(name.trim())) return 'living';
  return firstMatch(ROOM_CATEGORY_RULES, combined) ?? 'specialty';
}

export function isRoomCategory(value: string): value is RoomCategory {
  return (ROOM_CATEGORIES as readonly string[]).includes(value);
}

export function isTaskCategory(value: string): value is TaskCategory {
  return (TASK_CATEGORIES as readonly string[]).includes(value);
}

/** Tags the migration and the starter library agree on for a seed key. */
export function residentialTags(
  jobTypes: TemplateJobType[] = ['standard'],
  floorTypes: FloorType[] = [],
): TemplateTags {
  return { segments: ['residential'], jobTypes, floorTypes };
}

export function commercialTags(jobTypes: TemplateJobType[] = ['standard'], floorTypes: FloorType[] = []): TemplateTags {
  return { segments: ['commercial'], jobTypes, floorTypes };
}

export function bothSegmentTags(
  jobTypes: TemplateJobType[] = ['standard'],
  floorTypes: FloorType[] = [],
): TemplateTags {
  return { segments: ['residential', 'commercial'], jobTypes, floorTypes };
}
